import { RegionsEnum } from "./regions.enum"
import { RegionRoles, RolesEnum } from "./roles.enum"

/**
 * Condensed form of RegionRoles — each region maps to an array of
 * single-character role codes (first letter of each RolesEnum value).
 * Regions with empty role lists are omitted.
 */
export type EncodedRegionRoles = { [K in RegionsEnum]?: string[] }

export interface DecodedCustomClaims {
  roles: RolesEnum[]
  regionRoles: RegionRoles
}

export interface EncodedCustomClaims {
  roles: string[]
  regionRoles: EncodedRegionRoles
}

const ALL_ROLES: RolesEnum[] = Object.values(RolesEnum)

/**
 * Map of short code (first letter) -> RolesEnum.
 * Built once at module load. Throws if two roles share a first letter.
 */
const ROLE_CODE_TO_ROLE: Record<string, RolesEnum> = ALL_ROLES.reduce(
  (acc, role) => {
    const code = role.charAt(0)
    if (acc[code]) {
      throw new Error(
        `RolesEnum code collision: "${acc[code]}" and "${role}" both start with "${code}". ` +
        `Update the codec to use a longer/explicit code mapping.`,
      )
    }
    acc[code] = role
    return acc
  },
  {} as Record<string, RolesEnum>,
)

/** RolesEnum[] → single-character codes. Deduped, unknown values dropped. */
export function encodeRoles(roles: unknown): string[] {
  if (!Array.isArray(roles)) return []
  const encoded = roles
    .filter((role): role is RolesEnum =>
      typeof role === "string" && ALL_ROLES.includes(role as RolesEnum),
    )
    .map(role => role.charAt(0))
  return [...new Set(encoded)]
}

/**
 * Decode a role list to RolesEnum[].
 * Backward-compatible: accepts either
 *   - single-character codes (new format), or
 *   - full RolesEnum strings (legacy format).
 * Deduped. Unrecognized values are dropped.
 */
export function decodeRoles(codes: unknown): RolesEnum[] {
  if (!Array.isArray(codes)) return []
  const decoded = codes
    .map(value => {
      if (typeof value !== "string") return undefined
      if (ALL_ROLES.includes(value as RolesEnum)) return value as RolesEnum
      return ROLE_CODE_TO_ROLE[value]
    })
    .filter((role): role is RolesEnum => !!role)
  return [...new Set(decoded)]
}

/** RegionRoles → condensed form. Empty regions are omitted. */
export function encodeRegionRoles(regionRoles: RegionRoles | undefined): EncodedRegionRoles {
  const source = regionRoles ?? ({} as RegionRoles)
  const encoded: EncodedRegionRoles = {}
  for (const region of Object.keys(source) as RegionsEnum[]) {
    const list = encodeRoles(source[region])
    if (list.length > 0) {
      encoded[region] = list
    }
  }
  return encoded
}

/** Condensed region roles → RegionRoles. */
export function decodeRegionRoles(regionRoles: unknown): RegionRoles {
  const source = (regionRoles ?? {}) as EncodedRegionRoles
  const decoded = {} as RegionRoles
  for (const region of Object.keys(source) as RegionsEnum[]) {
    decoded[region] = decodeRoles(source[region])
  }
  return decoded
}

export function encodeCustomClaims(customClaims: DecodedCustomClaims): EncodedCustomClaims {
  return {
    roles: encodeRoles(customClaims.roles),
    regionRoles: encodeRegionRoles(customClaims.regionRoles),
  }
}

export function decodeCustomClaims(customClaims: {
  roles?: unknown
  regionRoles?: unknown
}): DecodedCustomClaims {
  return {
    roles: decodeRoles(customClaims.roles),
    regionRoles: decodeRegionRoles(customClaims.regionRoles),
  }
}
